import { ApiError } from '@clockwise/shared';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ImportSheet } from '@/components/custom/import-sheet';
import { skillsApi } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

interface SkillImportSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Domain wrapper around the generic `ImportSheet` for the skill catalog.
 * Identity is the `Name` column — a name not already in the catalog
 * inserts a new skill, a matching name updates that row (category,
 * description, type, status) in place.
 */
export function SkillImportSheet({ open, onOpenChange }: SkillImportSheetProps) {
  const queryClient = useQueryClient();

  return (
    <ImportSheet
      open={open}
      onOpenChange={onOpenChange}
      title="Import skills"
      description="Upload an .xlsx or .csv. Rows are matched to existing skills by Name."
      onImport={async (file) => {
        const res = await skillsApi.importFile(file);
        return {
          totalRows: res.data.totalRows,
          created: res.data.created,
          updated: res.data.updated,
          failed: res.data.failed,
          rows: res.data.rows.map((r) => ({
            rowNumber: r.rowNumber,
            outcome: r.outcome,
            errors: r.errors,
          })),
        };
      }}
      onSuccess={() => {
        queryClient.invalidateQueries({ queryKey: queryKeys.skills.all });
      }}
      onDownloadTemplate={async () => {
        try {
          const { blob, filename } = await skillsApi.exportTemplate();
          triggerDownload(blob, filename ?? 'skills-template.xlsx');
        } catch (err) {
          const msg = err instanceof ApiError ? err.message : 'Template download failed';
          toast.error(msg);
        }
      }}
    />
  );
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
